
import React, { useEffect, useState, useMemo, useRef } from "react";
import "./dashboard.scss";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { updateNewAssessmentVariable } from "../../../redux/slices/globalVariableSlice";
import Loading from "../../layout/loader/Loading";
import Navbar from "../../layout/header/Header";
import SearchInputBox from "../../layout/search/SearchInputBox";
import Pagination from "../../layout/pagination/Pagination";
import ProjectReadinessDashboard from "./ProjectReadinessDashboard";
import { elements } from "chart.js";
import axiosInstance from "../../../httpInterceptor";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { MdFilterAlt } from "react-icons/md";
import { BiTransfer } from "react-icons/bi";
import { LuArrowDownUp } from "react-icons/lu";
import { GoArrowUp } from "react-icons/go";
import { GoArrowDown } from "react-icons/go";
import { makeStyles } from "@mui/styles";
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  TableSortLabel,
  TablePagination,
  Checkbox,
  Select,
} from "@material-ui/core";
import { CheckBox, FirstPage, LastPage, FilterList, UnfoldMore } from "@mui/icons-material";
import { FaSort, FaSortUp, FaSortDown, FaSearch, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { FaSort as FaSort6 } from "react-icons/fa6";
import { current } from "@reduxjs/toolkit";
import axios from "axios";
import { Menu, MenuItem } from "@mui/material";
import { saveAs } from "file-saver";

const useStyles = makeStyles({
  pagination: {
    "& .MuiTablePagination-toolbar": {
      backgroundColor: "white",
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      color: "#333",
    },
    "& .MuiTablePagination-actions": {
      "& .MuiButtonBase-root": {
        color: "#007bff",
      },
    },
  },
  root: {
    color: "white",
    "&:hover": {
      color: "white",
    },
    "&.MuiTableSortLabel-active": {
      color: "white",
    },
  },
  icon: {
    color: "white !important",
  },
  tableCell: {
    borderBottom: "none",
    borderTop: "none",
  },
  customBorder: {
    borderBottom: "1px solid transparent",
    border: "0",
  },
  tableRow: {
    "& td, & th": {
      border: "0 px",
      borderBottom: "none",
    },
  },
});

const CustomPaginationActions = (props: any) => {
  const { count, page, rowsPerPage, onPageChange } = props;
  const handleFirstPageButtonClick = (event: any) => {
    onPageChange(event, 0);
  };
  const handleLastPageButtonClick = (event: any) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };
  return (
    <div style={{ flexShrink: 0, marginLeft: "2.5em" }}>
      <Button
        color="primary"
        style={{
          backgroundColor: "#074173",
          border: "1px solid grey",
          translate: "-15px",
          width: "6rem",
        }}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPage style={{ color: "white" }} />
      </Button>
      <Button
        color="primary"
        style={{
          backgroundColor: "#074173",
          translate: "-5px",
          border: "1px solid grey",
          width: "6rem",
        }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPage style={{ color: "white" }} />
      </Button>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const classes = useStyles();
  // Shared inline styles for filter dropdowns (cosmetic only)
  const menuStyle: React.CSSProperties = {
    position: 'absolute',
    maxHeight: '14rem',
    overflowY: 'auto',
    width: '12rem',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 6px 18px rgba(2,12,27,0.12)',
    zIndex: 1000,
    transform: 'translate(0px, 60px)',
    // padding: '8px',
    scrollbarWidth: 'thin',
    fontSize: '0.9rem',
    color: '#074173',
  };

  const accountMenuStyle: React.CSSProperties = {
    ...menuStyle,
    width: '15rem',
    transform: 'translate(-110px, 66px)'
  };

  // Helper to compute a fixed position menu style anchored to the button
  const computeMenuStyle = (anchor: HTMLElement | null, overrides?: React.CSSProperties) => {
    if (!anchor) return { ...menuStyle, position: 'absolute', transform: 'translate(0px,60px)' } as React.CSSProperties;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    // Default width from overrides or menuStyle
    let desiredWidth = overrides?.width || menuStyle.width || '12rem';
    // Convert width strings like '12rem' or '200px' to numeric px value (best-effort)
    const parseWidth = (w: any) => {
      if (!w) return 200;
      if (typeof w === 'number') return w;
      const s = String(w).trim();
      if (s.endsWith('px')) return parseFloat(s.replace('px', '')) || 200;
      if (s.endsWith('rem')) return (parseFloat(s.replace('rem', '')) || 12) * parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
      const n = parseFloat(s);
      return isNaN(n) ? 200 : n;
    };
    const menuWidthPx = parseWidth(desiredWidth as any);
    const DEBUG_MENU = false; // set true temporarily to log positioning

    // Place menu below the anchor and center it under the anchor by default
    const gap = 6;
    const top = rect.bottom + window.scrollY + gap; // small gap
    const anchorLeft = rect.left;
    const anchorRight = rect.right;

    // Left-align the menu under the anchor icon's left edge (small offset to avoid overlap)
    const leftOffset = 2; // small pixel offset
    let left = Math.round(anchorLeft + leftOffset);

    // If left-align would overflow to the right, shift left to keep within viewport
    if (left + menuWidthPx + 12 > viewportWidth) {
      left = Math.max(8, viewportWidth - menuWidthPx - 12);
    }

    // Ensure menu not off-screen on left
    if (left < 8) left = 8;

    // If there's very little space below, try placing above the anchor
    let finalTop = top;
    const menuHeightEstimate = Math.min(320, viewportHeight - 80);
    if (finalTop + menuHeightEstimate > window.scrollY + viewportHeight) {
      finalTop = rect.top + window.scrollY - menuHeightEstimate - gap;
      if (finalTop < 8) finalTop = 8;
    }

    if (DEBUG_MENU) {
      // eslint-disable-next-line no-console
      console.debug('computeMenuStyle', { rect, menuWidthPx, left, finalTop, viewportWidth, viewportHeight });
    }

    return {
      ...menuStyle,
      position: 'fixed',
      left,
      top: finalTop,
      transform: 'none',
      zIndex: 2000,
      width: desiredWidth,
      ...(overrides || {}),
    } as React.CSSProperties;
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#074173',
  };

  const checkboxStyle: React.CSSProperties = {
    width: '14px',
    height: '14px',
    accentColor: '#074173',
    cursor: 'pointer',
  };
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [showProjectReadiness, setShowProjectReadiness] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<string[]>([]);
  const [selectedAssessmentDate, setSelectedAssessmentDate] = useState<string[]>([]);
  const [expandedStartYears, setExpandedStartYears] = useState<string[]>([]);
  const [expandedAssessmentYears, setExpandedAssessmentYears] = useState<string[]>([]);
  // New: track expanded months per year for both start and assessment filters
  const [expandedStartMonths, setExpandedStartMonths] = useState<{ [year: string]: string[] }>({});
  const [expandedAssessmentMonths, setExpandedAssessmentMonths] = useState<{ [year: string]: string[] }>({});
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState("assessment_date");
  const [query, setQuery] = useState({
    projectid: "",
    set_name: "",
    account: "",
    duname: "",
    projectname: "",
    project_start_date: "",
    project_health: "",
    assessment_date: "",
    caseStudyCount: "",
    // caseStudyCount: "",
  });
  const [filteredproject, setFilteredProject] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [projects, setProjects] = useState(filteredproject);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [caseStudyCounts, setCaseStudyCounts] = useState<{ [key: string]: number }>({});
  const [llmReportUrls, setLlmReportUrls] = useState<{ [key: string]: { url: string | null } }>({});
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [showSyncToast, setShowSyncToast] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorEl1, setAnchorEl1] = useState<null | HTMLElement>(null);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const filterMenuRef1 = useRef<HTMLDivElement>(null);
  const filterMenuRef2 = useRef<HTMLDivElement>(null);
  const ignoreSortClickRef = useRef(false);
  // const [openUpload, setOpenUpload] = useState(false);
  // const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // const [openProcessPopup, setOpenProcessPopup] = useState(false);
  // const [processText, setProcessText] = useState("");

  let url;

  const handlequerychange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery({
      ...query,
      [e.target.name]: e.target.value,
    });
  };

  
  function convertToISODate(dateStr: any) {
    const [day, month, year] = dateStr.split("-");
    return `${month}-${day}-${year}`;
  }

 
  function formatStartDate(dateStr: any) {
    if (!dateStr) return "N/A";
    const raw = String(dateStr).split(" ")[0]; // drop time if present
    let parts: string[] | null = null;

    if (raw.includes("-")) parts = raw.split("-");
    else if (raw.includes("/")) parts = raw.split("/");
    else parts = null;

    if (parts && parts.length === 3) {
      // Handle common orders: YYYY-MM-DD, DD-MM-YYYY (or DD/MM/YYYY)
      // If first part is 4 characters, assume YYYY-MM-DD
      if (parts[0].length === 4) {
        const [year, month, day] = parts;
        const mm = String(month).padStart(2, "0");
        const dd = String(day).padStart(2, "0");
        return `${mm}-${dd}-${year}`;
      }
      // Otherwise assume format like DD-MM-YYYY (day first)
      const [day, month, year] = parts;
      const mm = String(month).padStart(2, "0");
      const dd = String(day).padStart(2, "0");
      return `${mm}-${dd}-${year}`;
    }

    const date = new Date(raw);
    if (isNaN(date.getTime())) return "N/A";
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  }

  function formatAssessmentDate(dateStr: any) {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  // Helper function to group dates by year
  const groupDatesByYear = (dates: string[]) => {
    const map: { [year: string]: { [month: string]: string[] } } = {};
    dates.forEach((date) => {
      const parts = date.split('-'); // [MM, DD, YYYY]
      if (parts.length !== 3) return;
      const [mm, dd, yyyy] = parts;
      if (!yyyy) return;
      if (!map[yyyy]) map[yyyy] = {};
      if (!map[yyyy][mm]) map[yyyy][mm] = [];
      // store full MM-DD-YYYY string
      map[yyyy][mm].push(date);
    });
    return map; // { year: { month: [dates] } }
  };

  // Convert month number ("01".."12") to month name ("Jan".."Dec")
  const monthNumberToName = (mm: string) => {
    const mapping: { [key: string]: string } = {
      '01': 'Jan',
      '02': 'Feb',
      '03': 'Mar',
      '04': 'Apr',
      '05': 'May',
      '06': 'Jun',
      '07': 'Jul',
      '08': 'Aug',
      '09': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec',
    };
    // Accept both '1' and '01'
    const padded = mm.padStart(2, '0');
    return mapping[padded] ?? mm;
  };

  // Toggle year expansion
  const toggleStartYearExpansion = (year: string) => {
    setExpandedStartYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  const toggleAssessmentYearExpansion = (year: string) => {
    setExpandedAssessmentYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );
  };

  // Toggle month expansion for start date
  const toggleStartMonthExpansion = (year: string, month: string) => {
    setExpandedStartMonths((prev) => {
      const months = prev[year] ?? [];
      const exists = months.includes(month);
      return {
        ...prev,
        [year]: exists ? months.filter((m) => m !== month) : [...months, month],
      };
    });
  };

  // Toggle month expansion for assessment date
  const toggleAssessmentMonthExpansion = (year: string, month: string) => {
    setExpandedAssessmentMonths((prev) => {
      const months = prev[year] ?? [];
      const exists = months.includes(month);
      return {
        ...prev,
        [year]: exists ? months.filter((m) => m !== month) : [...months, month],
      };
    });
  };

  // Toggle year selection (select/deselect all dates in that year)
  const toggleStartYearSelection = (year: string, datesInYearFlat: string[]) => {
    const allSelected = datesInYearFlat.every((date) => selectedStartDate.includes(date));
    if (allSelected) {
      setSelectedStartDate((prev) => prev.filter((date) => !datesInYearFlat.includes(date)));
    } else {
      setSelectedStartDate((prev) => {
        const newDates = datesInYearFlat.filter((d) => !prev.includes(d));
        return [...prev, ...newDates];
      });
    }
  };

  const toggleAssessmentYearSelection = (year: string, datesInYear: string[]) => {
    const allSelected = datesInYear.every((date) => selectedAssessmentDate.includes(date));
    if (allSelected) {
      setSelectedAssessmentDate((prev) => prev.filter((date) => !datesInYear.includes(date)));
    } else {
      setSelectedAssessmentDate((prev) => {
        const newDates = datesInYear.filter((d) => !prev.includes(d));
        return [...prev, ...newDates];
      });
    }
  };

  // Toggle month selection: selects/deselects all dates in a specific month
  const toggleStartMonthSelection = (year: string, month: string, datesInMonth: string[]) => {
    const allSelected = datesInMonth.every((d) => selectedStartDate.includes(d));
    if (allSelected) {
      setSelectedStartDate((prev) => prev.filter((d) => !datesInMonth.includes(d)));
    } else {
      setSelectedStartDate((prev) => {
        const newDates = datesInMonth.filter((d) => !prev.includes(d));
        return [...prev, ...newDates];
      });
    }
  };

  const toggleAssessmentMonthSelection = (year: string, month: string, datesInMonth: string[]) => {
    const allSelected = datesInMonth.every((d) => selectedAssessmentDate.includes(d));
    if (allSelected) {
      setSelectedAssessmentDate((prev) => prev.filter((d) => !datesInMonth.includes(d)));
    } else {
      setSelectedAssessmentDate((prev) => {
        const newDates = datesInMonth.filter((d) => !prev.includes(d));
        return [...prev, ...newDates];
      });
    }
  };

  // Ensure any date string becomes MM-DD-YYYY (if possible)
  const normalizeToMMDDYYYY = (dateStr: string) => {
    if (!dateStr) return dateStr;
    // Already MM-DD-YYYY?
    const parts = String(dateStr).split('-');
    if (parts.length === 3 && parts[0].length === 2 && parts[2].length === 4) return dateStr;
    // If format is DD-MM-YYYY or DD/MM/YYYY
    if (parts.length === 3) {
      // assume DD-MM-YYYY -> convert
      const [a, b, c] = parts;
      if (a.length === 2 && b.length === 2 && c.length === 4) {
        // could be DD-MM-YYYY -> return MM-DD-YYYY
        return `${b.padStart(2,'0')}-${a.padStart(2,'0')}-${c}`;
      }
    }
    // If contains '/', try Date parse
    const replaced = String(dateStr).replace(/\//g, '-');
    const d = new Date(replaced);
    if (!isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${mm}-${dd}-${yyyy}`;
    }
    return dateStr;
  };

  const formatUtcDateTime = (value?: string | null) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "N/A";
    try {
      const formatted = new Intl.DateTimeFormat("en-GB", {
        timeZone: "UTC",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
      return `${formatted} UTC`;
    } catch {
      return `${date.toISOString().replace("T", " ").slice(0, 16)} UTC`;
    }
  };

  useEffect(() => {
    if (!lastSyncAt) {
      setShowSyncToast(false);
      return;
    }
    setShowSyncToast(true);
    const id = setTimeout(() => {
      setShowSyncToast(false);
    }, 10000);
    return () => clearTimeout(id);
  }, [lastSyncAt]);

  useEffect(() => {
    setFilteredProject(projectData);
  }, [projectData]);

  useEffect(() => {
    console.log(`[STATE CHANGED] orderBy: ${orderBy}, order: ${order}`);
  }, [orderBy, order]);

  // Handle navigation state to show Project Readiness Dashboard
  useEffect(() => {
    if (location.state?.showProjectReadiness) {
      setShowProjectReadiness(true);
      // Clear the state to prevent it from persisting on subsequent visits
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location]);

  const accountOptions = Array.from(
    new Set(projectData.map((item) => item.accountName))
  ).filter(Boolean).sort((a: any, b: any) => {
    const aa = String(a).toLowerCase();
    const bb = String(b).toLowerCase();
    if (aa < bb) return -1;
    if (aa > bb) return 1;
    return 0;
  });

  const handleAccountFilter = (accounts: string[]) => {
    if (accounts && accounts.length === 0) {
      setFilteredProject([]);
      return;
    }
    if (!accounts) return;
    const lowered = accounts.map((a) => String(a).toLowerCase());
    const filtered = projectData.filter((item) =>
      item.accountName && lowered.includes(String(item.accountName).toLowerCase())
    );
    setFilteredProject(filtered);
  };

  const open = Boolean(anchorEl);
  const open1 = Boolean(anchorEl1);
  const open2 = Boolean(anchorEl2);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    // Filters are applied by effect when selectedAccount changes
  };

  const startDateOptions = Array.from(
    new Set(projectData.map((item) => item.start_date))
  );

  // Group start dates by year for hierarchical display
  const startDatesByYear = useMemo(() => {
    const formattedDates = startDateOptions
      .filter((date) => date)
      .map((date) => {
        const raw = String(date).split(" ")[0];
        return formatStartDate(raw);
      })
      .filter((d) => d && d !== "N/A");
    // groupDatesByYear expects MM-DD-YYYY strings
    return groupDatesByYear(formattedDates as string[]);
  }, [startDateOptions]);

  const handleStartDateFilter = (startDates: string[]) => {
    
    if (startDates && startDates.length === 0) {
      setFilteredProject([]);
      return;
    }
    if (!startDates) return;
    const lowered = startDates.map((d) => String(d));
    const filtered = projectData.filter((item) => {
      const raw = item.start_date ? String(item.start_date).split(" ")[0] : "";
      const formatted = raw ? formatStartDate(raw) : ""; // ensures MM-DD-YYYY
      return formatted && lowered.includes(formatted);
    });
    setFilteredProject(filtered);
  };

  const handleClick1 = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl1(anchorEl1 ? null : event.currentTarget);
  };
  const handleClose1 = () => {
    setAnchorEl1(null);
  };

  const assessmentDateOptions = Array.from(
    new Set(
      projectData
        .map((item) => item.assessment_date)
        .filter((date) => date) // Remove null/undefined
        .map((date) => formatAssessmentDate(date)) // Format to MM-DD-YYYY
    )
  ).sort();

  // Group assessment dates by year for hierarchical display
  const assessmentDatesByYear = useMemo(() => {
    // assessmentDateOptions already formatted as MM-DD-YYYY
    return groupDatesByYear(assessmentDateOptions);
  }, [assessmentDateOptions]);

  const handleAssessmentDateFilter = (assessmentDates: string[]) => {
    // If assessmentDates explicitly empty -> show no results. If undefined/null -> no-op.
    if (assessmentDates && assessmentDates.length === 0) {
      setFilteredProject([]);
      return;
    }
    if (!assessmentDates) return;
    const lowered = assessmentDates.map((d) => String(d));
    const filtered = projectData.filter((item) => {
      if (!item.assessment_date) return false;
      const formattedDate = formatAssessmentDate(item.assessment_date);
      return lowered.includes(formattedDate);
    });
    setFilteredProject(filtered);
  };

  const handleClick2 = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl2(anchorEl2 ? null : event.currentTarget);
  };
  const handleClose2 = () => {
    setAnchorEl2(null);
  };

  // Helper: toggle select all accounts
  const toggleSelectAllAccounts = () => {
    if (selectedAccount.length === accountOptions.length) {
      setSelectedAccount([]);
    } else {
      setSelectedAccount([...accountOptions]);
    }
  };

  // Helper: select/deselect all start dates
  const toggleSelectAllStartDates = () => {
    const allDates = Object.values(startDatesByYear).flatMap((m) => Object.values(m)).flat();
    if (allDates.length === 0) return;
    // allDates are in MM-DD-YYYY already
    const flat = allDates.map((d) => String(d));
    const allSelected = flat.every((d) => selectedStartDate.includes(d));
    if (allSelected) setSelectedStartDate([]);
    else setSelectedStartDate(flat);
  };

  // Helper: select/deselect all assessment dates
  const toggleSelectAllAssessmentDates = () => {
    const allDates = Object.values(assessmentDatesByYear).flatMap((m) => Object.values(m)).flat();
    if (allDates.length === 0) return;
    const flat = allDates.map((d) => String(d));
    const allSelected = flat.every((d) => selectedAssessmentDate.includes(d));
    if (allSelected) setSelectedAssessmentDate([]);
    else setSelectedAssessmentDate(flat);
  };

  // Toggle selection helpers for multi-select behavior
  const toggleAccountSelection = (account: string) => {
    setSelectedAccount((prev) =>
      prev.includes(account) ? prev.filter((a) => a !== account) : [...prev, account]
    );
  };

  const toggleStartDateSelection = (date: string) => {
    setSelectedStartDate((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const toggleAssessmentDateSelection = (date: string) => {
    setSelectedAssessmentDate((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  // Apply all active menu filters together
  const applyMenuFilters = () => {
    let filtered = [...projectData];

    // If selected arrays are present but empty -> that means user explicitly unchecked all -> no results
    if (selectedAccount && selectedAccount.length === 0) {
      setFilteredProject([]);
      return;
    }
    if (selectedAccount && selectedAccount.length > 0) {
      const lowered = selectedAccount.map((a) => String(a).toLowerCase());
      filtered = filtered.filter((item) =>
        item.accountName && lowered.includes(String(item.accountName).toLowerCase())
      );
    }

    if (selectedStartDate && selectedStartDate.length === 0) {
      setFilteredProject([]);
      return;
    }
    if (selectedStartDate && selectedStartDate.length > 0) {
      filtered = filtered.filter((item) => {
        const raw = item.start_date ? String(item.start_date).split(" ")[0] : "";
        const formatted = raw ? formatStartDate(raw) : "";
        return formatted && selectedStartDate.includes(formatted);
      });
    }

    if (selectedAssessmentDate && selectedAssessmentDate.length === 0) {
      setFilteredProject([]);
      return;
    }
    if (selectedAssessmentDate && selectedAssessmentDate.length > 0) {
      filtered = filtered.filter((item) => {
        if (!item.assessment_date) return false;
        const formattedDate = formatAssessmentDate(item.assessment_date);
        return selectedAssessmentDate.includes(formattedDate);
      });
    }

    setFilteredProject(filtered);
  };

  // Re-apply combined menu filters whenever selection arrays change
  useEffect(() => {
    applyMenuFilters();
    
  }, [selectedAccount, selectedStartDate, selectedAssessmentDate, projectData]);

  // Click outside handler to close filter menus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      let closedAny = false;

      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-filter-button="account"]')
      ) {
        setAnchorEl(null);
        closedAny = true;
      }
      if (
        filterMenuRef1.current &&
        !filterMenuRef1.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-filter-button="startDate"]')
      ) {
        setAnchorEl1(null);
        closedAny = true;
      }
      if (
        filterMenuRef2.current &&
        !filterMenuRef2.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-filter-button="assessmentDate"]')
      ) {
        setAnchorEl2(null);
        closedAny = true;
      }

      if (closedAny) {
        const oneTimeBlocker = (ev: MouseEvent) => {
          try {
            ev.stopPropagation();
            ev.preventDefault();
          } catch (err) {
            
          }
          document.removeEventListener('click', oneTimeBlocker as any, true);
        };
        document.addEventListener('click', oneTimeBlocker as any, true);
        // also set the ref as a fallback for handlers that check it
        ignoreSortClickRef.current = true;
        // clear the fallback shortly after to avoid interfering with user clicks
        setTimeout(() => {
          ignoreSortClickRef.current = false;
        }, 300);
      }
    };

    if (anchorEl || anchorEl1 || anchorEl2) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl, anchorEl1, anchorEl2]);

  useEffect(() => {
    console.log(query);
    let filtered = [...projectData]; // Create a fresh copy
    console.log(projectData);

    // Project ID filter
    if (query.projectid && query.projectid.trim()) {
      const projectIdStr = String(query.projectid).toLowerCase().trim();
      filtered = filtered.filter(
        (item: any) =>
          item.projectNumber &&
          String(item.projectNumber).toLowerCase().includes(projectIdStr)
      );
    }

    // Set name filter
    if (query.set_name && query.set_name.trim()) {
      filtered = filtered.filter(
        (item: any) =>
          item.set_name &&
          item.set_name.toLowerCase().includes(query.set_name.toLowerCase().trim())
      );
    }

    // Project name filter
    if (query.projectname && query.projectname.trim()) {
      const regex = new RegExp(query.projectname.toLowerCase().trim(), "i");
      filtered = filtered.filter(
        (item: any) =>
          item.projectName && regex.test(item.projectName.toLowerCase())
      );
    }

    // DU name filter
    if (query.duname && query.duname.trim()) {
      filtered = filtered.filter(
        (item: any) =>
          item.u_delivery_unit &&
          item.u_delivery_unit
            .toLowerCase()
            .includes(query.duname.toLowerCase().trim())
      );
    }

    // Account filter
    if (query.account && query.account.trim()) {
      filtered = filtered.filter(
        (item: any) =>
          item.accountname &&
          item.accountname.toLowerCase().includes(query.account.toLowerCase().trim())
      );
    }

   
    if (query.project_start_date && query.project_start_date.trim()) {
      const searchTerm = query.project_start_date.trim().toLowerCase();

      filtered = filtered.filter((item: any) => {
        const itemDate = item.start_date;

        // Support searching for N/A / NA / N
        if (searchTerm === "n/a" || searchTerm === "na" || searchTerm === "n") {
          if (!itemDate) return true;
          const formatted = formatStartDate(itemDate);
          return formatted === "N/A";
        }

        if (!itemDate) return false;

        const formattedItemDate = formatStartDate(itemDate);
        if (formattedItemDate === "N/A") return false;

        // Split into [MM, DD, YYYY]
        const dateParts = formattedItemDate.split("-");

        // If search contains '-', match full date pattern
        if (searchTerm.includes("-")) {
          return formattedItemDate.includes(searchTerm);
        } else {
          // Numeric input: check month/day or partial/full year
          const paddedSearch = searchTerm.padStart(2, "0");

          // Month/Day or last two digits of year
          if (searchTerm.length <= 2) {
            const matchesDayOrMonth =
              dateParts[0] === paddedSearch || dateParts[1] === paddedSearch;
            if (matchesDayOrMonth) return true;

            const lastTwoDigitsOfYear = dateParts[2].slice(-2);
            if (lastTwoDigitsOfYear === paddedSearch) return true;
          }

          // Year partial match from start
          return dateParts[2].startsWith(searchTerm);
        }
      });
    }

    // Project health filter (tolerant to variants like "NA", "N/A", "n/a", "na", "n")
    if (query.project_health && query.project_health.trim()) {
      const rawSearch = String(query.project_health).toLowerCase().trim();
      const normalizedSearch = rawSearch.replace(/[^a-z0-9]/g, "");
      const normalizeHealth = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
      if (normalizedSearch === "na" || normalizedSearch === "n") {
        filtered = filtered.filter((item: any) => {
          const h = normalizeHealth(item.health);
          return h === "" || h === "na";
        });
      } else {
        filtered = filtered.filter((item: any) => {
          const h = normalizeHealth(item.health);
          if (!h) return false;
          return h.includes(normalizedSearch) || normalizedSearch.includes(h);
        });
      } 
    }

    // Assessment date filter (unchanged)
    if (query.assessment_date && query.assessment_date.trim()) {
      filtered = filtered.filter((item: any) => {
        const itemDate = item.assessment_date;
        const searchTerm = query.assessment_date.trim().toLowerCase();

        // Check if user is searching for N/A
        if (searchTerm === 'n/a' || searchTerm === 'na' || searchTerm === 'n') {
          if (!itemDate) return true;
          const formattedDate = formatAssessmentDate(itemDate);
          return formattedDate === "N/A";
        }

        if (!itemDate) return false;

        const formattedItemDate = formatAssessmentDate(itemDate);
        if (formattedItemDate === "N/A") return false;

        // Split the formatted date into components: [MM, DD, YYYY]
        const dateParts = formattedItemDate.split("-");

        if (searchTerm.includes("-")) {
          // If search contains "-", match the full date pattern
          return formattedItemDate.includes(searchTerm);
        } else {
          // For numeric input, try matching against all date parts
          const paddedSearch = searchTerm.padStart(2, '0');

          // Check if matches day (DD) or month (MM) when length is 1-2
          if (searchTerm.length <= 2) {
            const matchesDayOrMonth = dateParts[0] === paddedSearch || dateParts[1] === paddedSearch;
            if (matchesDayOrMonth) return true;

            // Also check if matches last 2 digits of year (e.g., "25" matches 2025)
            const lastTwoDigitsOfYear = dateParts[2].slice(-2);
            if (lastTwoDigitsOfYear === paddedSearch) return true;
          }

          // Check if matches year (YYYY) with partial matching from start
          return dateParts[2].startsWith(searchTerm);
        }
      });
    }

    // Case Study Count filter
    if (query.caseStudyCount && query.caseStudyCount.trim()) {
      const searchTerm = query.caseStudyCount.trim().toLowerCase();
      filtered = filtered.filter((item: any) => {
        const key = `${item.projectNumber}_${item.set_name}`;
        const count = caseStudyCounts[key];
        const displayValue = count !== undefined ? String(count) : '-';
        
        // Support searching for '-' or variants of N/A to find projects with no case studies
        if (searchTerm === '-' || searchTerm === 'n/a' || searchTerm === 'na' || searchTerm === 'n') {
          return displayValue === '-';
        }
        
        // Numeric matching: search term should match the count value
        return displayValue.includes(searchTerm);
      });
    }

    setFilteredProject(filtered);
    console.log(query);
    console.log(filtered);
    setCurrentPage(1);
  }, [query, projectData]);

  const handleRowsPerPageChange = (rows: number) => {
    setProjectPerPage(rows);
    setCurrentPage(1); // Reset to first page when rows per page changes
  };

  // Sort the filtered projects first, before pagination
  // Use useMemo to ensure sorting only happens when dependencies change
  const sortedFilteredProjects = useMemo(() => {
    console.log(`[SORTING TRIGGERED] orderBy: ${orderBy}, order: ${order}, items: ${filteredproject.length}`);
    return [...filteredproject].sort((a, b) => {
      // Health sorting
      if (orderBy === "project_health") {
        const normalizeHealth = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const healthA = normalizeHealth(a.health);
        const healthB = normalizeHealth(b.health);

        const isNA_A = healthA === "na" || healthA === "";
        const isNA_B = healthB === "na" || healthB === "";
        if (isNA_A && isNA_B) return 0;
        if (isNA_A) return 1;
        if (isNA_B) return -1;

        // Ascending order: Red (1) -> Amber (2) -> Green (3)
        // Descending order: Green (1) -> Amber (2) -> Red (3)
        const healthPriority: { [key: string]: number } = {
          red: 1,
          amber: 2,
          green: 3
        };
        const priorityA = healthPriority[healthA] ?? 999;
        const priorityB = healthPriority[healthB] ?? 999;

        // asc = Red first, desc = Green first
        const result = order === "asc" ? priorityA - priorityB : priorityB - priorityA;
        return result;
      }

      if (orderBy === "start_date" || orderBy === "assessment_date") {
        const rawA = a[orderBy];
        const rawB = b[orderBy];

        // For start_date we keep the existing behavior (normalize then compare date-only)
        if (orderBy === "start_date") {
          const dateStrA = rawA ? String(rawA).split(" ")[0] : null;
          const dateStrB = rawB ? String(rawB).split(" ")[0] : null;
          const formattedA = dateStrA ? formatStartDate(dateStrA) : "N/A";
          const formattedB = dateStrB ? formatStartDate(dateStrB) : "N/A";

          const timeA = new Date(formattedA).getTime();
          const timeB = new Date(formattedB).getTime();

          const validA = !isNaN(timeA);
          const validB = !isNaN(timeB);

          if (!validA && !validB) return 0;
          if (!validA) return 1;
          if (!validB) return -1;

          return order === "asc" ? timeA - timeB : timeB - timeA;
        }

        // For assessment_date prefer the full timestamp (raw value) so that
        // assessments on the same day but added later (later time) appear first.
        const timeAraw = rawA ? new Date(rawA).getTime() : NaN;
        const timeBraw = rawB ? new Date(rawB).getTime() : NaN;

        let validAraw = !isNaN(timeAraw);
        let validBraw = !isNaN(timeBraw);

        // If raw parse failed, try falling back to the formatted display date
        let timeA = timeAraw;
        let timeB = timeBraw;
        if (!validAraw) {
          const fa = rawA ? formatAssessmentDate(rawA) : "N/A";
          timeA = new Date(fa).getTime();
          validAraw = !isNaN(timeA);
        }
        if (!validBraw) {
          const fb = rawB ? formatAssessmentDate(rawB) : "N/A";
          timeB = new Date(fb).getTime();
          validBraw = !isNaN(timeB);
        }

        // Place invalid/N/A at the end
        if (!validAraw && !validBraw) return 0;
        if (!validAraw) return 1;
        if (!validBraw) return -1;

        // If timestamps are equal (rare) fall back to stable tie-breaker using projectNumber
        if (timeA === timeB) {
          const idA = a.projectNumber ?? a.projectid ?? "";
          const idB = b.projectNumber ?? b.projectid ?? "";
          return order === "asc" ? String(idA).localeCompare(String(idB)) : String(idB).localeCompare(String(idA));
        }

        return order === "asc" ? timeA - timeB : timeB - timeA;
      }

      return order === "asc"
        ? a[orderBy]?.localeCompare(b[orderBy])
        : b[orderBy]?.localeCompare(a[orderBy]);
    });
  }, [filteredproject, orderBy, order]); // Re-calculate when any of these change

  // Log first few sorted items for debugging
  useEffect(() => {
    console.log(`[SORTED DATA] orderBy: ${orderBy}, order: ${order}`,
      sortedFilteredProjects.slice(0, 10).map(p => {
        let displayDate = p[orderBy];
        let timestamp = null;
        if (orderBy === 'start_date' && p[orderBy]) {
          const dateStr = String(p[orderBy]).split(" ")[0];
          const convertedDate = formatStartDate(dateStr);
          timestamp = new Date(convertedDate).getTime();
          displayDate = convertedDate;
        } else if (orderBy === 'assessment_date' && p[orderBy]) {
          timestamp = new Date(p[orderBy]).getTime();
          displayDate = formatAssessmentDate(p[orderBy]);
        } else if (p[orderBy]) {
          timestamp = new Date(p[orderBy]).getTime();
        }
        return {
          project: p.projectNumber,
          date: p[orderBy],
          displayDate: displayDate,
          timestamp: timestamp ?? 'null'
        };
      })
    );
  }, [sortedFilteredProjects, orderBy]);

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;

  const currentProjects = useMemo(() => {
    return sortedFilteredProjects.slice(
      indexOfFirstProject,
      indexOfLastProject
    );
  }, [sortedFilteredProjects, indexOfFirstProject, indexOfLastProject]);

  // Log what's being displayed on current page
  useEffect(() => {
    console.log(
      `[CURRENT PAGE DISPLAY] Page: ${currentPage}, Showing indices ${indexOfFirstProject}-${indexOfLastProject}`,
      currentProjects.map(p => ({
        project: p.projectNumber,
        date: p[orderBy],
        formatted: orderBy === 'assessment_date'
          ? formatAssessmentDate(p[orderBy])
          : orderBy === 'start_date'
          ? formatStartDate(String(p[orderBy]).split(" ")[0])
          : p[orderBy]
      }))
    );
  }, [currentProjects, currentPage, orderBy]);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const fetchProjectsBatch = async (lastKey?: string | null, isFirstBatch: boolean = false) => {
    try {
      const params: string[] = ["limit=30"];
      if (lastKey) {
        params.push(`lastKey=${encodeURIComponent(lastKey)}`);
      }
      const query = params.length ? `?${params.join("&")}` : "";

      const { data: rawDbData } = await axiosInstance.get(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/projects${query}`
      );

      const dbData = Array.isArray(rawDbData)
        ? rawDbData
        : (rawDbData && Array.isArray(rawDbData.items)
            ? rawDbData.items
            : []);

      const nextKey: string | null =
        rawDbData && typeof rawDbData.nextKey === "string" && rawDbData.nextKey.length > 0
          ? rawDbData.nextKey
          : null;

      if (!dbData || dbData.length === 0) {
        return;
      }

      // Prepare payload for bulk project status fetch for this batch
      const statusRequestPayload = dbData.map((project: any) => ({
        projectid: project.projectid,
        set_name: project.set_name,
      }));

      const projectStatusPromise = axiosInstance.post(
        `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/projects/health`,
        statusRequestPayload
      );

      const caseStudyRequestPayload = dbData.map((project: any) => ({
        projectid: project.projectid,
        set_name: project.set_name,
      }));

      const llmRequestPayload = dbData.map((project: any) => ({
        projectid: project.projectid,
        set_name: project.set_name,
      }));

      const [projectStatusResponse, caseStudyResponse, llmResponse] = await Promise.all([
        projectStatusPromise,
        axiosInstance
          .post(
            `${process.env.REACT_APP_ASSESSMENT_SERVICE_BASE_URL}/get-casestudy-count`,
            caseStudyRequestPayload
          )
          .catch((error) => {
            console.warn("Failed to fetch bulk case study counts:", error);
            return { data: { data: [] } } as any;
          }),
        axiosInstance
          .post(
            `${process.env.REACT_APP_LLM_SERVICE_BASE_URL}/report/latest-urls`,
            { items: llmRequestPayload }
          )
          .catch((error) => {
            console.warn("Failed to fetch assessment report URLs:", error);
            return { data: { results: [] } } as any;
          }),
      ]);

      // Case study counts for this batch
      try {
        const caseStudyResults =
          (caseStudyResponse as any)?.data?.data && Array.isArray((caseStudyResponse as any).data.data)
            ? (caseStudyResponse as any).data.data
            : [];

        const countsForBatch: { [key: string]: number } = {};
        caseStudyResults.forEach((item: any) => {
          if (!item || !item.projectid || !item.set_name) return;
          const key = `${item.projectid}_${item.set_name}`;
          const raw = item.casestudy_count;
          countsForBatch[key] =
            raw === null || raw === undefined || Number.isNaN(Number(raw))
              ? 0
              : Number(raw);
        });

        dbData.forEach((project: any) => {
          const key = `${project.projectid}_${project.set_name}`;
          if (countsForBatch[key] === undefined) {
            countsForBatch[key] = 0;
          }
        });

        setCaseStudyCounts((prev) => ({
          ...prev,
          ...countsForBatch,
        }));
      } catch (e) {
        console.warn("Error processing case study count response:", e);
      }

      // Assessment report latest URLs for this batch
      try {
        const llmResults =
          (llmResponse as any)?.data?.results && Array.isArray((llmResponse as any).data.results)
            ? (llmResponse as any).data.results
            : [];

        const urlMapForBatch: { [key: string]: { url: string | null } } = {};

        llmResults.forEach((item: any) => {
          if (!item || !item.projectid || !item.set_name) return;
          const key = `${item.projectid}_${item.set_name}`;
          const url = item.latest_url || "";
          urlMapForBatch[key] = { url };
        });

        dbData.forEach((project: any) => {
          const key = `${project.projectid}_${project.set_name}`;
          if (!urlMapForBatch[key]) {
            urlMapForBatch[key] = { url: "" };
          }
        });

        setLlmReportUrls((prev) => ({
          ...prev,
          ...urlMapForBatch,
        }));
      } catch (e) {
        console.warn("Error processing assessment report URL response:", e);
      }

      // Health statuses for this batch
      let projectStatuses;
      if (projectStatusResponse.data && projectStatusResponse.data.data) {
        projectStatuses = projectStatusResponse.data.data;
        console.log(`Health API returned paginated data: ${projectStatuses.length} items`);
      } else if (Array.isArray(projectStatusResponse.data)) {
        projectStatuses = projectStatusResponse.data;
      } else {
        console.error('Unexpected health API response format:', projectStatusResponse.data);
        projectStatuses = [];
      }

      const batchResponse = dbData.map((dbProject: any) => {
        const statusObj = projectStatuses.find(
          (status: any) =>
            status.projectid === dbProject.projectid &&
            status.set_name === dbProject.set_name
        );
        console.log("statusObj", statusObj);
        console.log("project", statusObj?.projstatus);
        return {
          ...dbProject,
          projectNumber: dbProject.projectid,
          projectName: dbProject.Projectname || dbProject.projectName || dbProject.projectname,
          accountName: dbProject.accountname || dbProject.accountName,
          u_delivery_unit: dbProject.duname || dbProject.u_delivery_unit,
          health: statusObj?.projstatus ?? "N/A",
          assessment_date: statusObj?.created_date ?? null,
        };
      });

      setProjectData((prev) => {
        const merged = [...prev, ...batchResponse];

        // Update last sync timestamp based on modified_date across all loaded projects
        try {
          let latest = 0;
          merged.forEach((proj: any) => {
            if (!proj.modified_date) return;
            const t = new Date(proj.modified_date).getTime();
            if (!isNaN(t) && t > latest) latest = t;
          });
          if (latest > 0) {
            setLastSyncAt(new Date(latest).toISOString());
          } else {
            setLastSyncAt(null);
          }
        } catch (e) {
          console.warn("Failed to derive last sync time from modified_date:", e);
          setLastSyncAt(null);
        }

        // Initialize default filter selections only once, when first data arrives
        if (prev.length === 0) {
          try {
            const accounts = Array.from(new Set(
              merged
                .map((r: any) => (r.accountName ? String(r.accountName).trim() : ""))
                .filter((a: string) => a.length > 0)
            )) as string[];
            if (accounts.length > 0) setSelectedAccount(accounts);

            const startDatesAll = Array.from(new Set(
              merged
                .map((r: any) => {
                  if (!r.start_date) return "";
                  const formatted = formatStartDate(String(r.start_date).split(" ")[0]);
                  return formatted && formatted !== "N/A" ? formatted : "";
                })
                .filter((d: string) => d.length > 0)
            )) as string[];
            if (startDatesAll.length > 0) setSelectedStartDate(startDatesAll);

            const assessmentDatesAll = Array.from(new Set(
              merged
                .map((r: any) =>
                  r.assessment_date ? String(formatAssessmentDate(r.assessment_date)) : ""
                )
                .filter((d: string) => d.length > 0)
            )) as string[];
            if (assessmentDatesAll.length > 0) setSelectedAssessmentDate(assessmentDatesAll);
          } catch (e) {
            console.warn('Failed to initialize default selections:', e);
          }
        }

        return merged;
      });

      // If there is another page, fetch it in the background
      if (nextKey) {
        fetchProjectsBatch(nextKey, false);
      }
    } catch (error: any) {
      console.error("Error fetching project data batch:", error);
      if (isFirstBatch) {
        setError(error.message);
      }
    } finally {
      if (isFirstBatch) {
        setLoading(false);
      }
    }
  };

  const fetchProjectsData = async () => {
    await fetchProjectsBatch(null, true);
  };

  useEffect(() => {
    fetchProjectsData();
  }, []);

  const handleNewAssessmentClick = () => {
    navigate("/dashboard/create-project");
  };

  const handleExportProjectDetails = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_DASHBOARD_SERVICE_BASE_URL}/projects/export`,
        { responseType: "arraybuffer" }
      );

      const contentType =
        (response.headers &&
          (response.headers["content-type"] ||
            response.headers["Content-Type"])) ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      let data = new Uint8Array(response.data as ArrayBuffer);
      let blobSource: Uint8Array | ArrayBuffer = data;

      const isZipHeader =
        data.length >= 4 &&
        data[0] === 0x50 &&
        data[1] === 0x4b &&
        data[2] === 0x03 &&
        data[3] === 0x04; // "PK\x03\x04"

      if (!isZipHeader) {
        const text = new TextDecoder("utf-8").decode(data).trim();
        if (text.startsWith("UEsD")) {
          const binaryString = window.atob(text);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i += 1) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blobSource = bytes;
        }
      }

      const blob = new Blob([blobSource as BlobPart], {
        type: contentType,
      });

      const timestamp = new Date().toISOString().slice(0, 10);
      saveAs(blob, `project-details-${timestamp}.xlsx`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to export project details:", err);
    }
  };

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return (
      <div className="error">
        <h3>Something went wrong!</h3>
      </div>
    );
  }

  const handleRequestSort = (property: any) => {
    // Ignore synthetic/accidental clicks that happen immediately after
    // closing a filter menu by clicking outside. The outside-mousedown
    // handler sets `ignoreSortClickRef` to true for the following click.
    if (ignoreSortClickRef.current) {
      // eslint-disable-next-line no-console
      console.debug('handleRequestSort: ignored due to recent menu close');
      return;
    }
    console.log(`[BEFORE CLICK] orderBy: ${orderBy}, order: ${order}, property: ${property}`);
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    console.log(`[AFTER CALCULATION] isAsc: ${isAsc}, newOrder: ${newOrder}`);
    setOrder(() => {
      console.log(`[SET ORDER] Setting to: ${newOrder}`);
      return newOrder;
    });
    setOrderBy(() => {
      console.log(`[SET ORDERBY] Setting to: ${property}`);
      return property;
    });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const CustomTooltip = styled(
    ({ className, title, children, ...props }: TooltipProps) => (
      <Tooltip {...props} classes={{ popper: className }} title={title}>
        {children}
      </Tooltip>
    )
  )({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "white",
      color: "#074173",
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
      fontSize: "14px",
    },
    [`& .MuiTooltip-arrow`]: {
      color: "white",
    },
  });

  const MyTooltip = styled(
    ({ className, title, children, ...props }: TooltipProps) => (
      <Tooltip {...props} classes={{ popper: className }} title={title}>
        {children}
      </Tooltip>
    )
  )({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "white",
      boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
      fontSize: "14px",
    },
  });

  const getSortIcon = (property: any) => {
    const isActive = orderBy === property;
    const iconType = !isActive ? 'neutral' : (order === 'asc' ? 'up' : 'down');

    if (property === 'assessment_date') {
      console.log(`[ICON] assessment_date - orderBy: ${orderBy}, order: ${order}, isActive: ${isActive}, icon: ${iconType}`);
    }

    // Custom sort icon with separate hover areas for up and down arrows with tooltips
    return (
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '14px', height: '14px' }}>
        <FaSort6 style={{ color: '#000', fontSize: '0.80rem', pointerEvents: 'none' }} />
        <Tooltip title="Click to sort ascending" placement="top" arrow>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '50%',
              cursor: 'pointer',
              zIndex: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRequestSort(property);
            }}
          />
        </Tooltip>
        <Tooltip title="Click to sort descending" placement="bottom" arrow>
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '50%',
              cursor: 'pointer',
              zIndex: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRequestSort(property);
            }}
          />
        </Tooltip>
      </div>
    );
  };

  const SortIconButton = styled(IconButton)`
    && {
      padding: 0;
      min-width: auto;
    }
    
    svg {
      transition: color 0.2s ease;
    }
    
    &:hover svg {
      color: #1976d2 !important;
    }
  `;

  return (
    <div className="dashboard_container">
      <Navbar fromProjectReadiness={showProjectReadiness} />
      <div className="app-pageSize">
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <div className="app-pageHeader" style={{ color: "#282828", fontSize: "1.2rem" }}>
              {showProjectReadiness ? "Project Readiness Dashboard" : "Project-Assessment List"}
            </div>
            {/* Toggle Switch */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "10px",
              backgroundColor: "#f0f4f8",
              padding: "6px 12px",
              borderRadius: "20px",
              border: "1px solid #d1d5db"
            }}>
              <span style={{ 
                fontSize: "0.85rem", 
                fontWeight: showProjectReadiness ? "normal" : "600",
                color: showProjectReadiness ? "#6b7280" : "#074173"
              }}>
                Project Assessment Dashboard
              </span>
              <label style={{ 
                position: "relative", 
                display: "inline-block", 
                width: "48px", 
                height: "24px",
                cursor: "pointer"
              }}>
                <input
                  type="checkbox"
                  checked={showProjectReadiness}
                  onChange={() => setShowProjectReadiness(!showProjectReadiness)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: "absolute",
                  cursor: "pointer",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: showProjectReadiness ? "#0d5eff" : "#cbd5e1",
                  transition: "0.4s",
                  borderRadius: "24px",
                }}>
                  <span style={{
                    position: "absolute",
                    content: '""',
                    height: "18px",
                    width: "18px",
                    left: showProjectReadiness ? "26px" : "3px",
                    bottom: "3px",
                    backgroundColor: "white",
                    transition: "0.4s",
                    borderRadius: "50%",
                  }}></span>
                </span>
              </label>
              <span style={{ 
                fontSize: "0.85rem", 
                fontWeight: showProjectReadiness ? "600" : "normal",
                color: showProjectReadiness ? "#074173" : "#6b7280"
              }}>
                Project Readiness Dashboard
              </span>
            </div>
          </div>
          {!showProjectReadiness && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", translate:'0px -15px'}}>
            <div style={{ display: "flex", gap: "10px", translate: "10px 5px" }}>
              <button
                onClick={handleNewAssessmentClick}
                style={{
                  height: "36px",
                  fontSize: "0.85rem",
                  background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                  borderRadius: "6px",
                  color: "#ffffff",
                  border: "none",
                  boxShadow: "0 1px 4px rgba(13,94,255,0.18)",
                  padding: "0 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  translate: '0px 20px'
                }}
              >
                <span>Log</span>
                <span>New</span>
                <span>Assessment</span>
                <span style={{ marginLeft: "5px" }}>+</span>
              </button>
              <button
                onClick={handleExportProjectDetails}
                style={{
                  height: "36px",
                  fontSize: "0.85rem",
                  background: "linear-gradient(135deg, #0d5eff 0%, #073fb1 100%)",
                  borderRadius: "6px",
                  color: "#ffffff",
                  border: "none",
                  boxShadow: "0 1px 4px rgba(13,94,255,0.18)",
                  padding: "0 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  translate: '0px 20px',
                  opacity: !filteredproject || filteredproject.length === 0 ? 0.6 : 1,
                }}
                disabled={!filteredproject || filteredproject.length === 0}
              >
                <span>Export Project Details</span>
              </button>
            </div>
            <div style={{ 
              display: "flex", 
              gap: "16px", 
              alignItems: "center",
              fontSize: "0.85rem",
              translate: '10px 0px',
              marginBottom: "10px",
              paddingTop:"20px"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ 
                  width: "20px", 
                  height: "20px", 
                  borderRadius: "50%", 
                  backgroundColor: "#4caf50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}>✓</div>
                <span style={{ color: "#000", fontWeight: 500 }}>Green</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ 
                  width: "20px", 
                  height: "20px", 
                  borderRadius: "50%", 
                  backgroundColor: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}>✕</div>
                <span style={{ color: "#000", fontWeight: 500 }}>Red</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{ 
                  width: "20px", 
                  height: "20px", 
                  borderRadius: "50%", 
                  backgroundColor: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: "bold"
                }}>!</div>
                <span style={{ color: "#000", fontWeight: 500 }}>Amber</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  backgroundColor: "#d1d5db",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // color: "#6b7280",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color:'white',
                  textAlign:'center',
                }}>–</div>
                <span style={{ color: "#000", fontWeight: 500 }}>N/A</span>
              </div>
            </div>
          </div>
          )}
        </div>

        {showProjectReadiness ? (
          <ProjectReadinessDashboard />
        ) : (
          <>
        {projectData.length > 0 ? (
          <div
            className="projectlistTableContainer"
            style={{ marginTop: "-30px" }}
          >
            <div className="projectlistTableContainer__wrapper">
              <table className="tstylee">
                <thead>
                <TableRow
                  style={{
                    borderTopLeftRadius: "0px",
                  }}
                >
                  {[
                    {
                      id: "projectNumber",
                      label: (
                        <span style={{ translate: '0px -8px' }}>
                          Project Id{" "}
                          <span style={{ fontSize: '0.7rem' }}>(PRJ ID)</span>
                        </span>
                      ),
                    },
                    { id: "projectName", label: <span style={{ translate: '0px -8px' }}>Project Name</span> },
                    { id: "u_delivery_unit", label: <span style={{ translate: '0px -8px' }}>DU</span> },
                    {
                      id: "accountName",
                      label: (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <span>Account</span>
                          <div>
                            <IconButton
                              style={{ background: "transparent", padding: '2px' }}
                              onClick={handleClick}
                              data-filter-button="account"
                            >
                              <CustomTooltip title="Filter">
                                <FilterList style={{ color: "black", fontSize: '1rem' }} />
                              </CustomTooltip>
                            </IconButton>

                            <Menu
                              id="account-filter-menu"
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl)}
                              onClose={handleClose}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                              MenuListProps={{ onMouseDown: (e: any) => e.stopPropagation(), onClick: (e: any) => e.stopPropagation() }}
                              PaperProps={{ ref: filterMenuRef, style: { width: '15rem', maxHeight: '14rem', overflowY: 'auto', zIndex: 2000 } }}
                              style={{translate:"0px 20px"}}
                            >
                              <div
                                className="custom-menu-item"
                                style={{ ...menuItemStyle, fontStyle: 'normal', justifyContent: 'flex-start' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  toggleSelectAllAccounts();
                                  const btn = (e.currentTarget as HTMLElement).closest('[data-filter-button="account"]');
                                  if (btn) {
                                    const iconBtn = (btn as HTMLElement).closest('button') as HTMLElement | null;
                                    if (iconBtn) {
                                      iconBtn.classList.add('filter-flash');
                                      setTimeout(() => iconBtn.classList.remove('filter-flash'), 400);
                                    }
                                  }
                                }}
                              >
                                <input type="checkbox" checked={selectedAccount.length === accountOptions.length && accountOptions.length>0} readOnly style={checkboxStyle} />
                                <span style={{ marginLeft: 3 }}>All</span>
                              </div>
                              {accountOptions.map((account) => (
                                <div
                                  key={account}
                                  className="custom-menu-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleAccountSelection(account);
                                  }}
                                  style={menuItemStyle}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedAccount.includes(account)}
                                    readOnly
                                    style={checkboxStyle}
                                  />
                                  <span>{account}</span>
                                </div>
                              ))}
                            </Menu>
                          </div>
                        </div>
                      ),
                    },
                    { id: "set_name", label: <span style={{ translate: '0px -8px',whiteSpace:'nowrap' }}>Assessment Type</span> },
                    {
                      id: "assessment_date",
                      label: (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                          <span style={{whiteSpace:'nowrap'}}>Assessment Date</span>
                          <span style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>(MM-DD-YYYY)</span>
                          <div>
                            <IconButton
                              style={{ background: "transparent", padding: '2px' }}
                              onClick={handleClick2}
                              data-filter-button="assessmentDate"
                            >
                              <CustomTooltip title="Filter">
                                <FilterList
                                  style={{
                                    color: "black",
                                    fontSize: '1rem',
                                  }}
                                />
                              </CustomTooltip>
                            </IconButton>

                            <Menu
                              id="assessment-filter-menu"
                              anchorEl={anchorEl2}
                              open={Boolean(anchorEl2)}
                              onClose={handleClose2}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                              MenuListProps={{ onMouseDown: (e: any) => e.stopPropagation(), onClick: (e: any) => e.stopPropagation() }}
                              PaperProps={{ ref: filterMenuRef2, style: { width: '12rem', maxHeight: '14rem', overflowY: 'auto', zIndex: 2000 } }}
                            >
                                <div
                                  className="custom-menu-item"
                                  style={{ ...menuItemStyle, fontStyle: 'normal', justifyContent: 'flex-start' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    toggleSelectAllAssessmentDates();
                                    // keep the assessment date menu open so user can continue selecting
                                    const btn = (e.currentTarget as HTMLElement).closest('[data-filter-button="assessmentDate"]');
                                    if (btn) {
                                      const iconBtn = (btn as HTMLElement).closest('button') as HTMLElement | null;
                                      if (iconBtn) {
                                        iconBtn.classList.add('filter-flash');
                                        setTimeout(() => iconBtn.classList.remove('filter-flash'), 400);
                                      }
                                    }
                                  }}
                                >
                                  <input type="checkbox" checked={(() => { const all = Object.values(assessmentDatesByYear).flatMap((m)=>Object.values(m)).flat(); return all.length>0 && all.every((d)=> selectedAssessmentDate.includes(String(d))) })()} readOnly style={checkboxStyle} />
                                  <span style={{ marginLeft: 3 }}>All</span>
                                </div>
                                {Object.keys(assessmentDatesByYear)
                                  .sort((a, b) => Number(b) - Number(a))
                                  .map((year) => {
                                  const monthsObj = assessmentDatesByYear[year];
                                  const datesInYearFlat = Object.values(monthsObj).flat();
                                  const allSelected = datesInYearFlat.every((date) => selectedAssessmentDate.includes(date));
                                  const someSelected = datesInYearFlat.some((date) => selectedAssessmentDate.includes(date));
                                  return (
                                  <div key={year}>
                                    <div
                                      className="custom-menu-item"
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          toggleAssessmentYearExpansion(year);
                                        }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleAssessmentYearExpansion(year);
                                      }}
                                      style={{ 
                                        ...menuItemStyle,
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        justifyContent: 'space-between'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                          type="checkbox"
                                          checked={allSelected}
                                          ref={(input) => {
                                            if (input) {
                                              input.indeterminate = someSelected && !allSelected;
                                            }
                                          }}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            toggleAssessmentYearSelection(year, datesInYearFlat);
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          style={checkboxStyle}
                                        />
                                        <span>{year}</span>
                                      </div>
                                      <span 
                                        style={{ width: '12px', flexShrink: 0, fontSize: '0.65rem', lineHeight: '1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                      >
                                        {expandedAssessmentYears.includes(year) ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                                      </span>
                                    </div>
                                    {expandedAssessmentYears.includes(year) && (
                                      <div style={{ paddingLeft: '24px' }}>
                                        {Object.keys(monthsObj).sort((a,b)=>Number(a)-Number(b)).map((mm) => {
                                          const datesInMonth = monthsObj[mm].sort((a,b)=> new Date(b).getTime() - new Date(a).getTime());
                                          const monthLabel = monthNumberToName(mm);
                                          const monthExpanded = (expandedAssessmentMonths[year] || []).includes(mm);
                                          const allMonthSelected = datesInMonth.every(d=> selectedAssessmentDate.includes(d));
                                          const someMonthSelected = datesInMonth.some(d=> selectedAssessmentDate.includes(d));
                                          return (
                                            <div key={`${year}-${mm}`}>
                                              <div className="custom-menu-item" style={{ ...menuItemStyle, justifyContent:'space-between' }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e)=>{
                                                  if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    toggleAssessmentMonthExpansion(year, mm);
                                                  }
                                                }}
                                                onClick={(e)=>{ e.stopPropagation(); toggleAssessmentMonthExpansion(year, mm); }}
                                              >
                                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                  <input
                                                    type="checkbox"
                                                    checked={allMonthSelected}
                                                    ref={(input)=>{ if(input){ input.indeterminate = someMonthSelected && !allMonthSelected } }}
                                                    onChange={(e)=>{ e.stopPropagation(); toggleAssessmentMonthSelection(year, mm, datesInMonth); }}
                                                    onClick={(e)=> e.stopPropagation()}
                                                    style={checkboxStyle}
                                                  />
                                                  <span>{monthLabel}</span>
                                                </div>
                                                <span style={{ width:'12px', flexShrink:0, fontSize: '0.65rem', lineHeight: '1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{ monthExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} /> }</span>
                                              </div>
                                              { monthExpanded && (
                                                <div style={{ paddingLeft: '20px' }}>
                                                  {datesInMonth.map((date)=> (
                                                    <div key={date} className="custom-menu-item" onClick={(e)=>{ e.stopPropagation(); toggleAssessmentDateSelection(date); }} style={{ ...menuItemStyle, whiteSpace:'nowrap' }}>
                                                      <input type="checkbox" checked={selectedAssessmentDate.includes(date)} readOnly style={checkboxStyle} />
                                                      <span style={{ whiteSpace:'nowrap' }}>{normalizeToMMDDYYYY(date)}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              ) }
                                            </div>
                                          )
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );})}
                            </Menu>
                          </div>
                        </div>
                      ),
                    },
                    {
                      id: "assessmentReport",
                      label: <span style={{ translate: '0px -8px', whiteSpace: 'nowrap' }}>Assessment Report</span>,
                      disableSort: true,
                    },
                    {
                      id: "project_health",
                      label: <span style={{ translate: '0px -8px' }}>Health</span>,
                      disableSort: false,
                    },
                    { id: "caseStudyCount", label: <span style={{ translate: '0px -8px', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: '1.2' }}><span>Related</span> <span>Case</span><span>Study</span><span>Count</span></span> },
                  ].map((header) => (
                    <TableCell
                      key={header.id}
                      style={{
                        fontSize: "0.8rem",
                        color: "black",
                        padding: "8px 6px",
                        textAlign: "center",
                        backgroundColor: "#D3D3D3",
                        verticalAlign: "middle",
                        lineHeight: "1.2",
                        height: "auto",
                        minWidth: "fit-content",
                        fontWeight: "600",
                        // borderBottom: "2px solid #b0b0b0",
                        letterSpacing: "0.3px",
                      }}
                      sortDirection={orderBy === header.id ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === header.id}
                        style={{ 
                          textAlign: "center", 
                          cursor: "default",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "auto",
                          height: "auto",
                        }}
                        direction={orderBy === header.id ? order : "asc"}
                        classes={{
                          root: classes.root,
                          icon: classes.icon,
                        }}
                        IconComponent={() => null}
                      >
                        <span
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "black",
                            fontWeight: "bold",
                            display: "inline-flex",
                            alignItems: "center",
                            verticalAlign: "middle",
                            lineHeight: "1.1",
                            cursor: 'default'
                          }}
                          className="header-cell"
                        >
                          {header.label}{" "}
                          {!(header as any).disableSort && (
                            <span
                              key={`${header.id}-${orderBy}-${order}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                marginLeft: "4px",
                                verticalAlign: "middle",
                                translate: ["projectNumber", "set_name", "projectName", "u_delivery_unit", "project_health", "accountName", "caseStudyCount", "assessmentReport", "assessment_date"].includes(header.id) ? "0px -8px" : undefined,
                              }}
                              className="sort-icon"
                            >
                              {getSortIcon(header.id)}
                            </span>
                          )}
                        </span>
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>

                <TableRow
                  style={{ height: "12px", borderBottom: "1px solid #e2e8f0", backgroundColor: "#f8fafc" }}
                >
                  <td
                    className="inputContainer2"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="projectid"
                      onChange={handlequerychange}
                      style={{
                        height: "30px",
                      }}
                      placeholder="Search"
                      aria-label="Search project ID"
                      
                    />
                    {query.projectid.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    className="inputContainer2"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="projectname"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search project name"
                    />
                    {query.projectname.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    className="inputContainer3"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="duname"
                      onChange={handlequerychange}
                      style={{
                        height: "30px",
                      }}
                      placeholder="Search"
                      aria-label="Search delivery unit"
                    />
                    {query.duname.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    className="inputContainer4"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="account"
                      onChange={handlequerychange}
                      style={{
                        height: "30px",
                      }}
                      placeholder="Search"
                      aria-label="Search account"
                    />
                    {query.account.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    className="inputContainer7"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="set_name"
                      onChange={handlequerychange}
                      style={{
                        height: "30px",
                      }}
                      placeholder="Search"
                      aria-label="Search assessment type"
                    />
                    {query.set_name.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    className="inputContainer7"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="assessment_date"
                      onChange={handlequerychange}
                      style={{ height: "30px" }}
                      placeholder="Search"
                      aria-label="Search assessment date"
                    />
                    {query.assessment_date.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    style={{
                      backgroundColor: "#f8fafc",
                      textAlign: "center",
                      fontSize: "0.75rem",
                      color: "#6b7280",
                    }}
                  >
                  </td>

                  <td
                    className="inputContainer5"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <input
                      type="text"
                      name="project_health"
                      onChange={handlequerychange}
                      // name="project_health"
                      // onChange={handlequerychange}
                      style={{
                        height: "30px",
                      }}
                      placeholder="Search"
                      aria-label="Search project health"
                      // aria-label="Search project health"
                    />
                    {query.project_health.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                    {query.project_health.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>

                  <td
                    className="inputContainer6"
                    style={{
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <input
                      type="text"
                      name="caseStudyCount"
                      className="caseStudyInput"
                      onChange={handlequerychange}
                      style={{
                        height: "30px",
                      }}
                      placeholder="Search"
                      aria-label="Search case study count"
                    />
                    {/* {query.caseStudyCount.length === 0 && ( */}
                    {query.caseStudyCount.length === 0 && (
                      <i
                        className="fa-solid fa-magnifying-glass"
                      ></i>
                    )}
                  </td>
                </TableRow>
              </thead>

              <tbody key={`tbody-${orderBy}-${order}-${currentPage}`} style={{ backgroundColor: "#ffffff" }}>
                {currentProjects.length === 0 ? (
                  <tr
                    style={{
                      overflow: "hidden",
                      height: "auto",
                      minHeight: "80px",
                    }}
                  >
                    <td
                      colSpan={9}
                      className="nodata"
                      style={{
                        backgroundColor: "#f8fafc",
                        color: "#64748b",
                        fontSize: "1rem",
                        fontWeight: "600",
                        textAlign:'center',
                        padding: "40px 20px",
                        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                      }}
                    >
                      No Record Found
                    </td>
                  </tr>
                ) : (
                  currentProjects.map((project: any, index: number) => (
                    <tr
                      key={`row-${indexOfFirstProject + index}-${project.projectNumber}-${project.set_name}`}
                      style={{
                        height: "40px",
                        overflow: "auto",
                        maxHeight: "60px",
                        borderBottom: "1px solid white",
                      }}
                    >
                      <td style={{ paddingLeft: "0.5%", textAlign: "center" }}>
                        <Link
                          style={{
                            color: " #074173",
                            fontFamily: "system-ui",
                          }}
                          to={`/dashboard/re-assessment/${project.projectNumber}/${encodeURIComponent(project.set_name)}`}
                          onClick={() => {
                            dispatch(updateNewAssessmentVariable(false));
                          }}
                        >
                          {project.projectNumber}
                        </Link>
                      </td>

                      <td
                        style={{
                          paddingLeft: "0.5%",
                          textAlign: "left",
                        }}
                      >
                        <CustomTooltip title={project.projectName}>
                          <span style={{ wordWrap: "break-word" }}>
                            {project.projectName}
                          </span>
                        </CustomTooltip>
                      </td>

                      <td
                        className="bodyContainer4"
                        style={{
                          textAlign: "center",
                        }}
                      >
                        {project.u_delivery_unit}
                      </td>

                      <td
                        style={{
                          paddingLeft: "0.5%",
                          textOverflow: "ellipsis",
                          textAlign: "left",
                        }}
                      >
                        <CustomTooltip title={project.accountName}>
                          <span>{project.accountName}</span>
                        </CustomTooltip> 
                      </td>

                      <td
                        style={{
                          textAlign: "center",
                        }}
                      >
                        {project.set_name}
                      </td>

                      <td style={{ paddingRight: "0.5%", textAlign: "center" }}>
                        {formatAssessmentDate(project.assessment_date)}
                      </td>

                      <td
                        style={{
                          textAlign: "center",
                        }}
                      >
                        {(() => {
                          const key = `${project.projectNumber}_${project.set_name}`;
                          const entry = llmReportUrls[key];
                          const url = entry?.url || "";
                          const hasUrl = url && url.trim().length > 0;

                          if (hasUrl) {
                            return (
                              <Button
                                variant="text"
                                color="primary"
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  window.open(url, "_blank", "noopener,noreferrer");
                                }}
                                style={{ textTransform: "none", fontWeight: 500 }}
                              >
                                View
                              </Button>
                            );
                          }

                          return (
                            <CustomTooltip title="No pdf is available">
                              <span>
                                <Button
                                  variant="text"
                                  size="small"
                                  disabled
                                  style={{
                                    textTransform: "none",
                                    fontWeight: 500,
                                    color: "#9ca3af",
                                  }}
                                >
                                  View
                                </Button>
                              </span>
                            </CustomTooltip>
                          );
                        })()}
                      </td>

                      <td style={{ paddingLeft: "0.5%", textAlign: "center"}}>
                        {(() => {
                          const h = String(project.health || "").toLowerCase();
                          if (h === "green") {
                            return (
                              <MyTooltip placement="top" title={<span style={{ color: "#16a34a" }}>Project Health is Green</span>}>
                                <div className="health-chip healthy">
                                  <span className="dot" style={{translate:'0px'}}>✓</span>
                                </div>
                              </MyTooltip>
                            );
                          }
                          if (h === "amber") {
                            return (
                              <MyTooltip placement="top" title={<span style={{ color: "#f59e0b" }}>Project Health is Amber</span>}>
                                <div className="health-chip warning">
                                  <span className="dot"  style={{translate:'0px'}}>!</span>
                                </div>
                              </MyTooltip>
                            );
                          } 
                          if (h === "red") {
                            return (
                              <MyTooltip placement="top" title={<span style={{ color: "red" }}>Project Health is Red</span>}>
                                <div className="health-chip critical">
                                  <span className="dot"  style={{translate:'0px'}}>X</span>
                                </div>
                              </MyTooltip>
                            );
                          }
                          return (
                            <div className="health-chip na">
                              <span className="dot">–</span>
                            </div>
                          );
                        })()}
                      </td>

                      <td
                        style={{
                          textAlign: "center",
                          color: "#515151ff",
                        }}
                      >
                        {(() => {
                          const key = `${project.projectNumber}_${project.set_name}`;
                          const count = caseStudyCounts[key];
                          const displayValue = count !== undefined ? count : '-';
                          
                          if (displayValue === '-') {
                            return displayValue;
                          }
                          
                          return (
                            <CustomTooltip title="Case Study count may vary, click to go to Case Study tab to get exact count">
                              <Link
                                to={`/dashboard/re-assessment/${project.projectNumber}/${encodeURIComponent(project.set_name)}`}
                                state={{ activeTab: "case_study" }}
                                style={{
                                  color: "#074173",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  fontWeight: "500"
                                }}
                                onClick={() => {
                                  dispatch(updateNewAssessmentVariable(false));
                                }}
                              >
                                {displayValue}
                              </Link>
                            </CustomTooltip>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* pagination component - fixed at bottom */}
            <div className="projectlistTableContainer__pagination">
              <Pagination
                projectsPerPage={projectsPerPage}
                totalProjects={filteredproject.length}
                paginate={paginate}
                currentPage={currentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          </div>
        ) : (
          // new assessment button implementation when there is no project assessment
          <div className="assessmentNotfound">
            No Assessment Found! Take new Assessment.
          </div>
        )}
        </>
        )}
      </div>
      {showSyncToast && lastSyncAt && (
        <div
          style={{
            position: "fixed",
            right: 24,
            bottom: 24,
            backgroundColor: "#111827",
            color: "#F9FAFB",
            padding: "8px 16px",
            borderRadius: 9999,
            boxShadow: "0 10px 25px rgba(15,23,42,0.35)",
            fontSize: "0.8rem",
            display: "flex",
            alignItems: "center",
            gap: 6,
            zIndex: 3000,
          }}
        >
          <span style={{ fontWeight: 500 }}>Last sync at:</span>
          <span>{formatUtcDateTime(lastSyncAt)}</span>
        </div>
      )}
    </div>
  );
};
export default Dashboard;



 